import * as React from 'react';
import type { AutomationAction } from '../../../../MedicareProAPI/Entities/Automation/AutomationAction';
import { AUTOMATION_ACTION_TYPE } from '../../../../MedicareProAPI/Entities/Automation/AutomationAction';
import {
  AUTOMATION_TRIGGER_RECIPE_LABELS,
  AUTOMATION_ACTION_TYPE_LABELS,
  AUTOMATION_ACTION_TYPE_ICONS,
  AUTOMATION_TRIGGER_DATA_FIELD_TYPE_LABELS,
} from '../../../Language/en';
import { Utilities } from '../../../../MedicareProAPI/Utilities/Utilities';
import { AutomationActionFormAction } from '../AutomationActionFormAction';
import { AutomationActionFormTrigger } from '../AutomationActionFormTrigger';
import { SelectMap } from '../../../Utilities/SelectMap';
import {
  AUTOMATION_ACTION_TRIGGER_TYPE,
  AUTOMATION_TRIGGER_COMBINATION,
  AUTOMATION_TRIGGER_DATA_FIELD_TYPE,
  Automation,
  AutomationActionTriggerData,
} from '../../../../MedicareProAPI/Entities/Automation/Automation';
import StyledModal from '../../StyledModal';
import { SIDETAB_POSITION, SideTab } from '../../SideTab';
import { AutomationActionCard } from './AutomationActionCard';

export interface AutomationActionGroup {
  key: number;
  items: AutomationActionGroupItem[];
}

export interface AutomationActionGroupItem {
  index: number;
  automationAction: AutomationAction;
}

export interface AutomationActionCardListProps {
  actions: AutomationAction[];
  nestedLevel?: number;
  onClick: (action?: AutomationAction) => void;
  onClickAddSubAction: (action?: AutomationAction) => void;
}

export const AutomationActionCardList = (
  props: AutomationActionCardListProps
): React.JSX.Element => {
  const [actions, setActions] = React.useState<AutomationAction[]>(null);
  const [nestedLevel] = React.useState<number>(props.nestedLevel || 0);
  const [groupedActions, setGroupActions] = React.useState<[AutomationActionGroupItem[]]>(null);

  React.useEffect(() => {
    setActions(props.actions);
  }, [props.actions]);

  React.useEffect(() => {
    let newGroupedActions = null;

    if (actions) {
      const tempGroupedActions = actions.reduce(
        (accumulator, automationAction: AutomationAction, automationActionIndex: number) => {
          // Define a unique key for this action
          let key;

          // If this action has a trigger criteria then group by the start delay
          if (automationAction.getTriggerCriteria()) {
            key = ''.concat(
              'grouped',
              automationAction.getStartDelayDays()
                ? automationAction.getStartDelayDays().toString()
                : '0'
            );
          }
          // Otherwise group by the action ID, which mean this will not be grouped
          else {
            key = ''.concat(automationAction.getId());
          }

          // Check to see if there are already actions grouped by this particular start delay
          const existingGroup = accumulator.find((currentGroup) => currentGroup.key === key);

          // Create the new object to represent this action
          const automationActionItem: AutomationActionGroupItem = {
            index: automationActionIndex,
            automationAction: automationAction,
          };

          // If no group exists then add a new one
          if (!existingGroup) {
            const newAutomationActionGroup: AutomationActionGroup = {
              key: key,
              items: [automationActionItem],
            };

            accumulator.push(newAutomationActionGroup);
          }
          // If the group already exists then appen this action to the group
          else {
            existingGroup.items.push(automationActionItem);
          }

          return accumulator;
        },
        []
      );

      newGroupedActions = Array.from(
        tempGroupedActions,
        (arrayItem: AutomationActionGroup) => arrayItem.items
      );
    }

    setGroupActions(newGroupedActions);
  }, [actions]);

  return (
    <>
      {groupedActions &&
        groupedActions.map(
          (groupedActionsSingle: AutomationActionGroupItem[], groupedActionsIndex) => {
            return (
              <React.Fragment key={groupedActionsIndex}>
                <h5 className="text-center mb-3 text-contrast">
                  <i className="fas fa-arrow-down" />
                </h5>
                <div className="row">
                  {groupedActionsSingle &&
                    groupedActionsSingle.map((actionItem: AutomationActionGroupItem) => {
                      const action = actionItem.automationAction;
                      const actionIndex = actionItem.index;

                      return (
                        <div key={actionIndex} className="col">
                          <AutomationActionCard
                            key={action.getId()}
                            nestedLevel={nestedLevel}
                            action={action}
                            onClick={(updateAction) => {
                              props.onClick(updateAction);
                            }}
                            onClickAddSubAction={(parentAction) => {
                              props.onClickAddSubAction(parentAction);
                            }}
                          />
                          {action.getSubActions().getTotalItems() > 0 && (
                            <AutomationActionCardList
                              actions={action.getSubActions().getItems()}
                              nestedLevel={nestedLevel + 1}
                              onClick={(updateAction) => {
                                props.onClick(updateAction);
                              }}
                              onClickAddSubAction={(parentAction) => {
                                props.onClickAddSubAction(parentAction);
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                </div>
              </React.Fragment>
            );
          }
        )}
    </>
  );
};
